import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MessageService } from 'primeng/api';
import { EstadoService } from '../../../services/configuracoes/estado.service';
import { Pais } from '../../../enums/pais.enum';
import { Router } from '@angular/router';
import { EstadoDTO, EstadoFormDTO } from '../../../models/configuracoes/estado.model';
import { TranslateService } from '@ngx-translate/core';
import { TableColumn } from '../../../models/shared/table-column.model';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-configuracao-estado',
  templateUrl: './configuracao-estado.component.html',
  styleUrls: ['./configuracao-estado.component.scss'],
  providers: [MessageService],
})
export class ConfiguracaoEstadoComponent implements OnInit, OnDestroy {
  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  // Controle de validacao visual
  isFormSubmitted = false;
  deleteDialogVisible = false;
  deleteItemId: number | null = null;

  // Objetos de traducao
  texts: any = {};
  labels: any = {};
  placeholders: any = {};
  botoes: any = {};
  tooltips: any = {};

  private langChangeSub!: Subscription;

  paises: { label: string; value: Pais; flagUrl: string }[] = [];
  estados: EstadoDTO[] = [];
  estadosOriginal: EstadoDTO[] = [];
  form: EstadoFormDTO = this.createEmptyForm();
  filterName = '';
  loading = true;
  first3 = 0;
  rows = 10;
  totalRecords = 0;
  lastfield = 'id';
  lastSort = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'configuracao-estado-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'configuracao-estado.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'configuracao-estado.labels.nome', visible: true, fixed: false, defaultVisible: true },
    { key: 'pais', labelKey: 'configuracao-estado.labels.pais', visible: true, fixed: false, defaultVisible: true },
    { key: 'codigoFaturaEletronica', labelKey: 'configuracao-estado.labels.codigo_fatura_eletronica', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'configuracao-estado.labels.data_da_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'configuracao-estado.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  private search$ = new Subject<string>();

  constructor(
    private messageService: MessageService,
    private estadoService: EstadoService,
    private translate: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.paises = this.getPaises();
    this.findEstado();
    this.setupSearchDebounce();
    this.loadTranslations();
    this.loadColumnPreferences();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('configuracao-estado.texts.titulo');
      this.texts.subtitulo = this.translate.instant('configuracao-estado.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'configuracao-estado.texts.titulo_editar' : 'configuracao-estado.texts.titulo'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'configuracao-estado.texts.subtitulo_editar' : 'configuracao-estado.texts.subtitulo_novo'
      );
    }
  }

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;

    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'configuracao-estado.texts.titulo' : isEdicao ? 'configuracao-estado.texts.titulo_editar' : 'configuracao-estado.texts.titulo'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'configuracao-estado.texts.subtitulo_lista'
          : isEdicao ? 'configuracao-estado.texts.subtitulo_editar' : 'configuracao-estado.texts.subtitulo_novo'
      ),
      dados_do_estado: this.translate.instant('configuracao-estado.texts.dados_do_estado'),
      tabela_de_estados: this.translate.instant('configuracao-estado.texts.tabela_de_estados'),
      nenhum_estado: this.translate.instant('configuracao-estado.texts.nenhum_estado'),
      ajuste_filtros: this.translate.instant('configuracao-estado.texts.ajuste_filtros'),
      carregando: this.translate.instant('configuracao-estado.texts.carregando'),
      confirmacao: this.translate.instant('common.confirmacao'),
      confirmar_exclusao: this.translate.instant('Certeza_que_deseja_excluir_este_estado?'),
      aba_cadastro: this.translate.instant('configuracao-estado.texts.aba_cadastro'),
      aba_lista: this.translate.instant('configuracao-estado.texts.aba_lista'),
    };

    this.labels = {
      id: this.translate.instant('configuracao-estado.labels.id'),
      nome: this.translate.instant('configuracao-estado.labels.nome'),
      codigo_fatura_eletronica: this.translate.instant('configuracao-estado.labels.codigo_fatura_eletronica'),
      pais: this.translate.instant('configuracao-estado.labels.pais'),
      data_da_inclusao: this.translate.instant('configuracao-estado.labels.data_da_inclusao'),
      acoes: this.translate.instant('configuracao-estado.labels.acoes'),
      itens_por_pagina: this.translate.instant('configuracao-estado.labels.itens_por_pagina'),
    };

    this.placeholders = {
      nome: this.translate.instant('configuracao-estado.placeholders.nome'),
      codigo_fatura_eletronica: this.translate.instant('configuracao-estado.placeholders.codigo_fatura_eletronica'),
      selecione: this.translate.instant('configuracao-estado.placeholders.selecione'),
      pesquisar: this.translate.instant('configuracao-estado.placeholders.pesquisar'),
    };

    this.botoes = {
      salvar: this.translate.instant('configuracao-estado.botoes.salvar'),
      atualizar: this.translate.instant('configuracao-estado.botoes.atualizar'),
      limpar: this.translate.instant('configuracao-estado.botoes.limpar'),
      cancelar: this.translate.instant('common.cancelar'),
      confirmar: this.translate.instant('common.confirmar'),
      novo_estado: this.translate.instant('configuracao-estado.botoes.novo_estado'),
    };

    this.tooltips = {
      editar: this.translate.instant('configuracao-estado.tooltips.editar'),
      excluir: this.translate.instant('configuracao-estado.tooltips.excluir'),
    };
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  getPaises(): { label: string; value: Pais; flagUrl: string }[] {
    return Object.values(Pais).map((pais) => ({
      label: pais,
      value: pais,
      flagUrl: this.getPaisFlagUrl(pais),
    }));
  }

  getPaisFlagUrl(pais: Pais): string {
    const paisStr = String(pais || '').toUpperCase();
    switch (paisStr) {
      case 'PARAGUAY':       return 'assets/images/paraguay-flag.jpg';
      case 'BRASIL':         return 'assets/images/br-flag.svg';
      case 'ARGENTINA':      return 'assets/images/argentina-flag.svg';
      case 'CHILE':          return 'assets/images/chile-flag.svg';
      case 'URUGUAY':        return 'assets/images/uruguay-flag.svg';
      case 'ESTADOS_UNIDOS': return 'assets/images/usa-flag.svg';
      default:               return '';
    }
  }

  findEstado(page: number = 0, size: number = this.rows, sort: string = 'id,asc') {
    this.loading = true;
    this.estadoService.get('/estado', { page, size, sort }).subscribe({
      next: (response) => {
        this.estadosOriginal = response.content || [];
        this.estados = [...this.estadosOriginal];
        this.totalRecords = response.totalElements || this.estadosOriginal.length;
        this.first3 = page * size;
        this.loading = false;

        // Reaplicar filtro local se houver termo de busca ativo
        if (this.filterName && this.filterName.trim().length > 0) {
          this.filtrarEstadosLocal(this.filterName.trim());
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('Erro_ao_carregar_estados'),
        });
        this.loading = false;
      },
    });
  }

  onPageChange(event: any) {
    const page = event.page;
    const size = event.rows;
    this.rows = event.rows;
    this.findEstado(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange() {
    this.first3 = 0;
    this.findEstado(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  onSortChange(event: any): void {
    if (!event.field) return;

    const field = event.field;
    const order = event.order === 1 ? 'asc' : 'desc';

    // Evitar chamada duplicada se mesmo campo e mesma direcao
    if (this.lastfield === field && this.lastSort === order) return;

    this.lastfield = field;
    this.lastSort = order;

    // Ordenar no backend (todos os registros do banco)
    this.first3 = 0;
    this.findEstado(0, this.rows, `${field},${order}`);
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  private setupSearchDebounce() {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.first3 = 0;
      if (term && term.trim().length > 0) {
        this.filtrarEstadosLocal(term.trim());
      } else {
        this.estados = [...this.estadosOriginal];
        this.totalRecords = this.estadosOriginal.length;
      }
    });
  }

  onSearchChange(value: string) {
    this.filterName = value || '';
    this.search$.next(this.filterName);
  }

  /** Filtra estados localmente buscando em todas as colunas visiveis */
  private filtrarEstadosLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.estados = this.estadosOriginal.filter(estado => {
      if (this.isColumnVisible('id') && estado.id != null) {
        if (String(estado.id).includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && estado.nome) {
        if (estado.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('pais') && estado.pais) {
        const paisNome = this.asEnumString(estado.pais);
        if (paisNome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('codigoFaturaEletronica') && estado.codigoFaturaEletronica) {
        if (estado.codigoFaturaEletronica.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && (estado as any).criadoEm) {
        const dataFormatada = new Date((estado as any).criadoEm).toLocaleDateString('pt-BR');
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.estados.length;
  }

  submit() {
    this.isFormSubmitted = true;

    if (!this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_nome_obrigatorio'),
      });
      return;
    }
    if (!this.form.codigoFaturaEletronica) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_codigo_fatura_obrigatorio'),
      });
      return;
    }
    if (!this.form.pais) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_pais_obrigatorio'),
      });
      return;
    }

    const body: EstadoDTO = {
      nome: this.form.nome,
      codigoFaturaEletronica: this.form.codigoFaturaEletronica,
      pais: this.form.pais.value,
    };

    this.estadoService.post('/estado', body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Estado_salvo_com_sucesso'),
        });
        this.isFormSubmitted = false;
        this.form = this.createEmptyForm();
        this.loadTranslations();
        this.activeTabIndex = 1;
        this.findEstado();
      },
      error: () => {},
    });
  }

  salvarEdicaoEstado() {
    this.isFormSubmitted = true;

    if (!this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_nome_obrigatorio'),
      });
      return;
    }
    if (!this.form.codigoFaturaEletronica) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_codigo_fatura_obrigatorio'),
      });
      return;
    }
    if (!this.form.pais) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('Campo_pais_obrigatorio'),
      });
      return;
    }

    const body: EstadoDTO = {
      id: this.form.id,
      nome: this.form.nome,
      codigoFaturaEletronica: this.form.codigoFaturaEletronica,
      pais: this.form.pais.value,
    };

    this.estadoService.put(`/estado/${this.form.id}`, body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Estado_editado_com_sucesso'),
        });
        this.isFormSubmitted = false;
        this.limparForm();
        this.activeTabIndex = 1;
        this.findEstado();
      },
      error: () => {},
    });
  }

  deletarEstado(id: number) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (this.deleteItemId == null) return;
    this.estadoService.delete(`/estado/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('Estado_excluido_com_sucesso'),
        });
        this.findEstado();
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
      error: () => {
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  limparForm() {
    this.isFormSubmitted = false;
    this.form = this.createEmptyForm();
    this.loadTranslations();
  }

  createEmptyForm(): EstadoFormDTO {
    return {
      nome: '',
      codigoFaturaEletronica: '',
      pais: undefined,
    };
  }

  private asEnumString(v: any): string {
    if (v && typeof v === 'object' && 'enum' in v) return String(v.enum);
    return String(v ?? '');
  }

  editarEstado(event: EstadoDTO) {
    const paisEnum = this.asEnumString(event.pais);

    const pais = this.paises.find(opt =>
      String(opt.value) === paisEnum || String(opt.label) === paisEnum
    );

    this.form = {
      id: event.id,
      nome: event.nome,
      codigoFaturaEletronica: event.codigoFaturaEletronica,
      pais: pais ?? undefined,
    };

    this.activeTabIndex = 0;
    this.texts.titulo = this.translate.instant('configuracao-estado.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('configuracao-estado.texts.subtitulo_editar');
    this.cdr.detectChanges();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  novoEstado(): void {
    this.limparForm();
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
